/**
 * Card Templates Service
 * 
 * API service for managing card templates. Templates define the
 * visual design and default greeting text for greeting cards.
 * User templates map to one or more catalog event types via {@link CreateCardTemplateRequest.eventTypeSlugs}.
 */

import apiClient from './apiClient';
import { CATALOG_PAGE_SIZE } from '../config/catalogPaging';
import type {
  CardTemplate,
  CreateCardTemplateRequest,
  UpdateCardTemplateRequest,
  PlaceholderInfo,
  UploadMediaResponse,
} from '../types';
import {
  MEDIA_FRAME_FORM_FIELD,
  hydrateCardTemplateCatalogPage,
  hydrateMediaFrameFields,
  type MediaFrame,
} from '../types/mediaFrame';

// Re-export types used by components consuming this service
export type { UploadMediaResponse };

/** Paged authenticated card-template response returned by PBI #1118. */
export interface CardTemplateCatalogPage {
  templates: CardTemplate[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
}

/** Options for {@link getCardTemplates}. */
export interface CardTemplateCatalogOptions {
  eventTypeId?: number;
  /** Catalog tag slugs — server-side filter for chip rows. */
  tagSlugs?: string[];
  /** When true, return only the signed-in user's custom templates (TemplatesPage "Show my e-cards only"). */
  ownerOnly?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * Fetches a paged card-template catalog (auth), optionally filtered by event type / tags.
 * PBI #1118 — browse and modal pickers append pages via infinite scroll.
 */
export const getCardTemplates = async (
  options: CardTemplateCatalogOptions = {}
): Promise<CardTemplateCatalogPage> => {
  const {
    eventTypeId,
    tagSlugs,
    ownerOnly = false,
    page = 1,
    pageSize = CATALOG_PAGE_SIZE,
  } = options;

  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (ownerOnly) params.set('ownerOnly', 'true');
  if (eventTypeId != null) params.set('eventTypeId', String(eventTypeId));
  for (const slug of tagSlugs ?? []) {
    if (slug.trim()) params.append('tagSlugs', slug.trim());
  }

  const response = await apiClient.get<CardTemplateCatalogPage>(
    `/cardtemplates?${params.toString()}`
  );

  // Normalize MediaFrameJson → mediaFrame so every picker/preview sees the crop.
  return hydrateCardTemplateCatalogPage(response.data);
};

/**
 * Fetches a single card template by ID.
 * @param id - The template ID
 * @returns Promise resolving to the card template
 */
export const getCardTemplate = async (id: number): Promise<CardTemplate> => {
  const response = await apiClient.get<CardTemplate>(`/cardtemplates/${id}`);
  return hydrateMediaFrameFields(response.data);
};

/**
 * Generates card content by merging template with a custom message.
 * @param templateId - The template ID
 * @param customMessage - Optional custom message to include
 * @returns Promise resolving to the generated content
 */
export const generateCardContent = async (
  templateId: number,
  customMessage?: string
): Promise<string> => {
  const response = await apiClient.post<{ content: string }>(
    `/cardtemplates/${templateId}/generate`,
    { customMessage }
  );
  return response.data.content;
};

/**
 * Creates a user-owned card template.
 * @param request - The template creation request
 * @returns Promise resolving to the created template
 */
export const createCardTemplate = async (
  request: CreateCardTemplateRequest
): Promise<CardTemplate> => {
  const response = await apiClient.post<CardTemplate>('/cardtemplates', request);
  return hydrateMediaFrameFields(response.data);
};

/**
 * Deletes a user-owned card template.
 * @param templateId - The template ID
 * @returns Promise resolving when delete is complete
 */
export const deleteCardTemplate = async (templateId: number): Promise<void> => {
  await apiClient.delete(`/cardtemplates/${templateId}`);
};

/**
 * Updates a user-owned card template.
 * Only the template owner can update their templates.
 * @param templateId - The template ID to update
 * @param request - The fields to update (only provided fields are changed)
 * @returns Promise resolving to the updated card template
 */
export const updateCardTemplate = async (
  templateId: number,
  request: UpdateCardTemplateRequest
): Promise<CardTemplate> => {
  const response = await apiClient.put<CardTemplate>(`/cardtemplates/${templateId}`, request);
  return hydrateMediaFrameFields(response.data);
};

/**
 * Fetches available template placeholders.
 * Returns a list of placeholders that can be used in greeting text.
 * @returns Promise resolving to array of placeholder information
 */
export const getPlaceholders = async (): Promise<PlaceholderInfo[]> => {
  const response = await apiClient.get<PlaceholderInfo[]>('/cardtemplates/placeholders');
  return response.data;
};

/**
 * Uploads media (image or video) for a template.
 * Replaces any existing media for the template.
 * Maximum file size is 50 MB.
 * 
 * @param templateId - The template ID to upload media for
 * @param file - The file to upload (image or video)
 * @param onProgress - Optional callback for upload progress (0-100)
 * @returns Promise resolving to the upload response with media URL
 */
export const uploadTemplateMedia = async (
  templateId: number,
  file: File,
  onProgress?: (percent: number) => void,
  mediaFrame?: MediaFrame | null,
): Promise<UploadMediaResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  if (mediaFrame) {
    formData.append(MEDIA_FRAME_FORM_FIELD, JSON.stringify(mediaFrame));
  }

  const response = await apiClient.post<UploadMediaResponse>(
    `/cardtemplates/${templateId}/media`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    }
  );

  return response.data;
};

/**
 * Updates only the media frame for a user template without re-uploading (PBI #1161).
 */
export const updateTemplateMediaFrame = async (
  templateId: number,
  mediaFrame: MediaFrame,
): Promise<MediaFrame> => {
  const response = await apiClient.patch<MediaFrame>(
    `/cardtemplates/${templateId}/media/frame`,
    { mediaFrame },
  );
  return response.data;
};

/**
 * Deletes media from a template without deleting the template itself.
 * 
 * @param templateId - The template ID to delete media from
 * @returns Promise resolving when delete is complete
 */
export const deleteTemplateMedia = async (templateId: number): Promise<void> => {
  await apiClient.delete(`/cardtemplates/${templateId}/media`);
};

/**
 * Gets the full URL for a media token.
 * Use this to display uploaded media in img/video elements.
 * 
 * Uses the configured API base URL to ensure media loads from the correct
 * domain (api-dev.scheduledsmiles.com or api.scheduledsmiles.com) rather
 * than the frontend domain (dev.scheduledsmiles.com).
 * 
 * @param mediaToken - The media token from a CardTemplate
 * @returns The full URL to fetch the media
 */
export const getMediaUrl = (mediaToken: string): string => {
  // Prefer VITE_MEDIA_URL when set (Azure Front Door host), fall back to
  // VITE_API_URL for local dev / E2E / rollback. Normalise so the helper
  // works whether the env var includes a trailing /api or not — both
  // formats are intentionally supported per the Card-Image-CDN plan.
  const baseUrl = resolveMediaBaseUrl();
  return `${baseUrl}/media/${mediaToken}`;
};

/**
 * Resolves the base URL used to build /api/media/{token} URLs.
 *
 * Order of precedence:
 *   1. VITE_MEDIA_URL (Azure Front Door host, e.g.
 *      https://media.scheduledsmiles.com or .../api)
 *   2. VITE_API_URL (direct origin, e.g. http://localhost:5180/api)
 *   3. /api (relative fallback for the dev Vite proxy)
 *
 * If the chosen base does not already end in /api, it is appended so the
 * existing template-string callers (`${base}/media/${token}`) keep working
 * unchanged. Returning a normalised value lets us keep this helper a
 * one-line read without per-call branching.
 */
const resolveMediaBaseUrl = (): string => {
  const explicit = import.meta.env.VITE_MEDIA_URL as string | undefined;
  if (explicit) {
    const trimmed = explicit.replace(/\/$/, '');
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
  }
  return import.meta.env.VITE_API_URL || '/api';
};

// ============================================================================
// Admin Template Media Functions
// ============================================================================

/**
 * Uploads media (image or video) to a system template via admin endpoint.
 * Replaces any existing media. Maximum file size is 50 MB.
 * 
 * This is the admin version that bypasses ownership checks and works with
 * system templates that have no owner.
 * 
 * @param slug - The system template slug identifier (e.g. TPL-001)
 * @param file - The file to upload (image or video)
 * @param onProgress - Optional callback for upload progress (0-100)
 * @returns Promise resolving to the upload response with media URL
 */
export const uploadAdminTemplateMedia = async (
  slug: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<UploadMediaResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<UploadMediaResponse>(
    `/admin/card-templates/${slug}/media`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    }
  );

  return response.data;
};

/**
 * Deletes media from a system template via admin endpoint.
 * 
 * This is the admin version that bypasses ownership checks and works with
 * system templates that have no owner.
 * 
 * @param slug - The system template slug identifier (e.g. TPL-001)
 * @returns Promise resolving when delete is complete
 */
export const deleteAdminTemplateMedia = async (slug: string): Promise<void> => {
  await apiClient.delete(`/admin/card-templates/${slug}/media`);
};

/**
 * Reorders system templates within one catalog event type (junction SortOrder).
 * Body matches {@code ReorderRequest.OrderedSlugs} — full slug list for that partition.
 *
 * @param eventTypeSlug - EVT-NNN catalog slug
 * @param orderedSlugs - Template slugs in display order (every template in that event type exactly once)
 */
export const reorderAdminTemplatesForEventType = async (
  eventTypeSlug: string,
  orderedSlugs: string[],
): Promise<void> => {
  await apiClient.put(
    `/admin/card-templates/event-type/${encodeURIComponent(eventTypeSlug)}/order`,
    { orderedSlugs },
  );
};

/**
 * Partial update for a system template (admin). Only fields present in the body are applied.
 * Used for inline carousel and tag edits on the E-Card Templates accordion table.
 *
 * @param slug - TPL-NNN catalog slug
 * @param payload - Subset of {@link UpdateCardTemplateRequest}
 */
export const updateAdminSystemTemplate = async (
  slug: string,
  payload: Partial<UpdateCardTemplateRequest>,
): Promise<void> => {
  await apiClient.put(`/admin/card-templates/${encodeURIComponent(slug)}`, payload);
};
